import { useState, useEffect, useLayoutEffect, useRef, useCallback, useContext, createContext } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation as useTranslationBase } from 'react-i18next';
import { st as stTxt } from '../lib/solicitudesTxt';
import { Link, useNavigate } from 'react-router-dom';
import {
  Flower2, Wind, Moon, Flame, Bed, Sparkles, ArrowRight, Calendar, MapPin, Users,
  Instagram, Play, Clock, Monitor, Compass, ChevronLeft, ChevronRight, X, Images, Mail, Check,
} from 'lucide-react';
import { API_BASE, fetchHorarios } from '../lib/api';
import { ahoraEnMadrid, yaEmpezada, aISO, parseFecha } from '../lib/fechas';
import { useAuth } from '../context/AuthContext';
import { useVideos, VideoCard, ReproductorModal } from '../components/VideosComunes';
import { BannerMembresia } from './Videos';
import EmpresasCTA from '../components/EmpresasCTA';

// Textos de portada editables desde el admin (Textos de portada). Cualquier t(clave)
// de esta página usa el texto del admin si existe; si no, el de la traducción por defecto.
const TextosPortadaCtx = createContext({});
function useTranslation() {
  const base = useTranslationBase();
  const textos = useContext(TextosPortadaCtx);
  const baseT = base.t;
  const t = useCallback((clave, opts) => (typeof clave === 'string' && textos[clave]) || baseT(clave, opts), [textos, baseT]);
  return { ...base, t };
}

const ICON_MAP = {
  'flower': Flower2,
  'flower-2': Flower2,
  'wind': Wind,
  'moon': Moon,
  'flame': Flame,
  'bed': Bed,
  'sparkles': Sparkles,
};

function ClaseIcon({ name, ...props }) {
  const Icon = ICON_MAP[name] || Flower2;
  return <Icon {...props} />;
}

function formatEuros(n) {
  const num = Number(n);
  if (Number.isInteger(num)) return `${num} €`;
  return `${num.toFixed(2).replace('.', ',')} €`;
}

function getInitials(name) {
  if (!name) return '';
  return name.trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
}

function useFetchJson(url) {
  const [state, setState] = useState({ data: null, error: null });
  useEffect(() => {
    let cancelled = false;
    setState({ data: null, error: null });
    fetch(url)
      .then((r) => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then((json) => { if (!cancelled) setState({ data: json.data || [], error: null }); })
      .catch((e) => { if (!cancelled) setState({ data: null, error: e.message }); });
    return () => { cancelled = true; };
  }, [url]);
  return state;
}

// Duración que verá el alumno al reservar: la de los horarios activos
// (rango si hay varias); si la clase no tiene horarios, la del tipo.
function textoDuracion(clase, t) {
  const ds = clase.duraciones_horario || [];
  if (ds.length > 1) return t('clases.duracion_rango', { min: ds[0], max: ds[ds.length - 1] });
  const m = ds[0] || clase.duracion_minutos;
  return m ? t('clases.duracion_min', { min: m }) : null;
}

function textoFormato(clase, t) {
  const fs = clase.formatos_horario || [];
  if (!fs.includes('online')) return null;
  return fs.includes('presencial') ? t('clases.formato_tambien_online') : t('clases.formato_online');
}

function ClaseCard({ clase }) {
  const { t } = useTranslation();
  const duracion = textoDuracion(clase, t);
  const formato = textoFormato(clase, t);
  // Card clickable si hay slug. Usamos Link envolviendo el article para que
  // todo el área sea clicable y mantenga semántica de navegación.
  const inner = (
    <>
      <div className="clase-head">
        <ClaseIcon name={clase.icono_nombre} size={32} strokeWidth={1.2} className="clase-icon" />
        <span className={'clase-nivel clase-nivel--' + clase.nivel}>
          {t('clases.nivel_' + clase.nivel)}
        </span>
      </div>
      <h3 className="clase-title">{clase.nombre}</h3>
      {(duracion || formato) && (
        <div className="clase-meta">
          {duracion && (
            <span className="clase-meta-item"><Clock size={13} strokeWidth={1.5} /> {duracion}</span>
          )}
          {formato && (
            <span className="clase-meta-item"><Monitor size={13} strokeWidth={1.5} /> {formato}</span>
          )}
        </div>
      )}
      <p className="clase-desc">{clase.descripcion}</p>
      {clase.slug && (
        <span className="clase-leer-mas">
          {t('clases.leer_mas')} <ArrowRight size={14} strokeWidth={1.5} />
        </span>
      )}
    </>
  );
  if (clase.slug) {
    return (
      <Link to={`/clases/${clase.slug}`} className="clase-card clase-card--link">
        {inner}
      </Link>
    );
  }
  return <article className="clase-card">{inner}</article>;
}

// Tarjeta de orientación: ocupa el hueco que deja la última fila de la
// rejilla (o una fila entera si no sobra hueco), así nunca hay celdas vacías.
function EmpezarCard({ span }) {
  const { t } = useTranslation();
  return (
    <Link
      to="/horarios"
      className="clase-card clase-card--link clase-card--empezar"
      style={{ gridColumn: `span ${span}` }}
    >
      <div className="clase-head">
        <Compass size={32} strokeWidth={1.2} className="clase-icon" />
      </div>
      <h3 className="clase-title">{t('clases.empezar_titulo')}</h3>
      <p className="clase-desc">{t('clases.empezar_texto')}</p>
      <span className="clase-leer-mas">
        {t('clases.empezar_cta')} <ArrowRight size={14} strokeWidth={1.5} />
      </span>
    </Link>
  );
}

// Nº de columnas reales de la rejilla (auto-fit), recalculado al redimensionar.
function useColumnasGrid(ref, deps) {
  const [cols, setCols] = useState(1);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const medir = () => {
      const n = getComputedStyle(el).gridTemplateColumns.split(' ').filter(Boolean).length;
      setCols(n || 1);
    };
    medir();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return cols;
}

function ClasesSection() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const { data, error } = useFetchJson(`${API_BASE}/api/clases-tipos?idioma=${lang}`);
  const loading = data === null && !error;
  const gridRef = useRef(null);
  const cols = useColumnasGrid(gridRef, [loading, error]);
  const resto = data ? data.length % cols : 0;
  const spanEmpezar = resto === 0 ? cols : cols - resto;

  return (
    <section id="clases" className="clases">
      <header className="section-head">
        <span className="section-eyebrow">{t('clases.eyebrow')}</span>
        <h2 className="section-title">{t('clases.titulo')}</h2>
      </header>
      {error ? (
        <div className="clases-error">
          {t('clases.error_carga')}
          <button className="clases-retry" onClick={() => window.location.reload()}>
            {t('comun.reintentar')}
          </button>
        </div>
      ) : (
        <div className="clases-grid" ref={gridRef}>
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="clase-card clase-skeleton" aria-hidden="true" />
              ))
            : (
              <>
                {data.map((c) => <ClaseCard key={c.id} clase={c} />)}
                {data.length > 0 && <EmpezarCard span={spanEmpezar} />}
              </>
            )}
        </div>
      )}
    </section>
  );
}

function BonoCard({ bono, popular }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const creditosTexto = bono.creditos_incluidos == null
    ? t('bonos.ilimitado')
    : t('bonos.clases', { count: bono.creditos_incluidos });
  const irAFicha = () => {
    if (bono.slug) navigate(`/bonos/${bono.slug}`);
  };
  return (
    <article className={'bono-card' + (popular ? ' bono-card--popular' : '')}>
      {popular && <div className="bono-badge">{t('bonos.popular_badge')}</div>}
      <div className="bono-head">
        <h3 className="bono-title">{bono.nombre}</h3>
        <div className="bono-creditos">{creditosTexto}</div>
      </div>
      <div className="bono-precio">{formatEuros(bono.precio_euros)}</div>
      {bono.validez_dias != null && (
        <div className="bono-validez">{t('bonos.validez', { dias: bono.validez_dias })}</div>
      )}
      <p className="bono-desc">{bono.descripcion}</p>
      <button
        type="button"
        className={'bono-cta' + (popular ? ' bono-cta--popular' : '')}
        onClick={irAFicha}
        disabled={!bono.slug}
      >
        {t('bonos.cta_comprar')} <ArrowRight size={14} strokeWidth={1.5} />
      </button>
    </article>
  );
}

function BonosSection() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const { data, error } = useFetchJson(`${API_BASE}/api/bonos-tipos?idioma=${lang}`);
  const loading = data === null && !error;
  return (
    <section id="bonos" className="bonos">
      <header className="section-head">
        <span className="section-eyebrow">{t('bonos.eyebrow')}</span>
        <h2 className="section-title">{t('bonos.titulo')}</h2>
      </header>
      {error ? (
        <div className="clases-error">
          {t('bonos.error_carga')}
          <button className="clases-retry" onClick={() => window.location.reload()}>
            {t('comun.reintentar')}
          </button>
        </div>
      ) : (
        <div className="bonos-grid">
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bono-card bono-skeleton" aria-hidden="true" />
              ))
            : data.map((b) => (
                <BonoCard key={b.id} bono={b} popular={b.orden_visualizacion === 3} />
              ))}
        </div>
      )}
    </section>
  );
}

function ProfesorCard({ profesor }) {
  return (
    <article className="profesor-card">
      <div className="profesor-avatar">
        {profesor.foto_url ? (
          <img src={profesor.foto_url} alt={profesor.nombre_publico} className="profesor-foto" />
        ) : (
          <span className="profesor-iniciales">{getInitials(profesor.nombre_publico)}</span>
        )}
      </div>
      <div className="profesor-body">
        <h3 className="profesor-nombre">{profesor.nombre_publico}</h3>
        <p className="profesor-bio">{profesor.bio_corta}</p>
        {profesor.especialidades?.length > 0 && (
          <ul className="profesor-chips">
            {profesor.especialidades.map((esp) => (
              <li key={esp} className="profesor-chip">{esp}</li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}

function ProfesoresSection() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const { data, error } = useFetchJson(`${API_BASE}/api/profesores?idioma=${lang}`);
  const loading = data === null && !error;
  return (
    <section id="profesores" className="profesores">
      <header className="section-head">
        <span className="section-eyebrow">{t('profesores.eyebrow')}</span>
        <h2 className="section-title">{t('profesores.titulo')}</h2>
      </header>
      {error ? (
        <div className="clases-error">
          {t('profesores.error_carga')}
          <button className="clases-retry" onClick={() => window.location.reload()}>
            {t('comun.reintentar')}
          </button>
        </div>
      ) : (
        <div className="profesores-grid">
          {loading
            ? Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="profesor-card profesor-skeleton" aria-hidden="true" />
              ))
            : data.map((p) => <ProfesorCard key={p.id} profesor={p} />)}
        </div>
      )}
    </section>
  );
}

// ============================================================================
// mejora-web F3: datos de portada (textos editables, promoción, galería)
// ============================================================================
function usePortada(lang) {
  const [portada, setPortada] = useState({ contenido: {}, promocion: null, galeria: [], cargado: false });
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/portada?idioma=${lang}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
      .then((j) => {
        if (!cancelled) setPortada({ contenido: j.contenido || {}, promocion: j.promocion || null, galeria: j.galeria || [], cargado: true });
      })
      .catch(() => { if (!cancelled) setPortada((p) => ({ ...p, cargado: true })); });
    return () => { cancelled = true; };
  }, [lang]);
  return portada;
}

// Texto editable desde el admin con fallback al texto por defecto (i18n).
function useTexto(contenido) {
  const { t } = useTranslation();
  return useCallback((clave, opts) => contenido[clave] || t(clave, opts), [contenido, t]);
}

// Próximas N clases desde ahora (hoy + 13 días), sin las ya empezadas.
function useProximasClases(lang, n = 3) {
  const [estado, setEstado] = useState({ data: null, error: null });
  useEffect(() => {
    let cancelled = false;
    const hoy = ahoraEnMadrid().fecha;
    const fin = parseFecha(hoy);
    fin.setUTCDate(fin.getUTCDate() + 13);
    fetchHorarios({ desde: hoy, hasta: aISO(fin), idioma: lang })
      .then((j) => {
        if (cancelled) return;
        const lista = (j.data || [])
          .filter((i) => !yaEmpezada(i.fecha_clase, i.hora_inicio) && i.permite_reserva_online)
          .slice(0, n);
        setEstado({ data: lista, error: null });
      })
      .catch((e) => { if (!cancelled) setEstado({ data: null, error: e.message }); });
    return () => { cancelled = true; };
  }, [lang, n]);
  return estado;
}

function formatDiaClase(fechaISO, lang, t) {
  const hoy = ahoraEnMadrid().fecha;
  const manana = aISO(new Date(parseFecha(hoy).getTime() + 86400000));
  if (fechaISO === hoy) return t('proximas.hoy');
  if (fechaISO === manana) return t('proximas.manana');
  const map = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  return new Intl.DateTimeFormat(map[lang] || 'es-ES', { weekday: 'short', day: 'numeric', timeZone: 'UTC' })
    .format(parseFecha(fechaISO));
}

function ProximasClasesCard() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const { data, error } = useProximasClases(lang, 3);

  return (
    <div className="proximas-card">
      <div className="proximas-head">
        <span className="proximas-titulo">{t('proximas.titulo')}</span>
        <Link to="/horarios" className="proximas-ver">{t('proximas.ver_todas')}</Link>
      </div>
      {error ? (
        <p className="proximas-vacio">{t('proximas.error')}</p>
      ) : data === null ? (
        <div className="proximas-lista">
          {[0, 1, 2].map((i) => <div key={i} className="proxima-fila proxima-skel" />)}
        </div>
      ) : data.length === 0 ? (
        <p className="proximas-vacio">{t('proximas.vacio')}</p>
      ) : (
        <div className="proximas-lista">
          {data.map((c) => {
            const libres = c.plazas_libres;
            const ultimas = typeof libres === 'number' && libres > 0 && libres <= 3;
            return (
              <div key={c.horario_id + c.fecha_clase} className="proxima-fila">
                <div className="proxima-cuando">
                  <span className="proxima-dia">{formatDiaClase(c.fecha_clase, lang, t)}</span>
                  <span className="proxima-hora">{c.hora_inicio}</span>
                </div>
                <div className="proxima-info">
                  <span className="proxima-clase">{c.clase_tipo.nombre}</span>
                  {typeof libres === 'number' && (
                    <span className={'proxima-plazas' + (ultimas ? ' is-ultimas' : '')}>
                      {ultimas ? t('reservas.ultimas_plazas', { count: libres }) : t('proximas.plazas', { count: libres })}
                    </span>
                  )}
                </div>
                <Link
                  to={`/horarios?dia=${c.fecha_clase}&reservar=${c.horario_id}`}
                  className="proxima-btn"
                >
                  {t('reservas.btn_reservar')}
                </Link>
              </div>
            );
          })}
        </div>
      )}
      <Link to="/clase-particular" className="horarios-particular horarios-particular--home"><span>{stTxt(lang, 'cta')}</span><strong>→</strong></Link>
    </div>
  );
}

// Aviso en la portada cuando hay un evento próximo a la venta (con plazas).
function AvisoEventoPortada() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const [ev, setEv] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/eventos?idioma=${lang}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
      .then((j) => { if (!cancelled) setEv((j.proximos || []).find((e) => e.plazas_restantes !== 0) || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [lang]);
  if (!ev) return null;
  return (
    <Link to={`/eventos/${ev.slug}`} className="aviso-evento">
      <span className="aviso-evento-tag">{t('eventos.aviso_nuevo')}</span>
      <span className="aviso-evento-nombre">{ev.nombre}</span>
      <span className="aviso-evento-fecha">{formatFechaCorta(ev.fecha, lang)}</span>
      <span className="aviso-evento-ver">{t('eventos.aviso_ver')} <ArrowRight size={13} strokeWidth={1.5} /></span>
    </Link>
  );
}

function HeroPortada({ portada }) {
  const tx = useTexto(portada.contenido);
  const { t } = useTranslation();
  const foto = portada.galeria[0];
  const promo = portada.promocion;

  return (
    <section className="hero2">
      <div className="hero2-inner">
        <div className="hero2-texto">
          <AvisoEventoPortada />
          <span className="hero-eyebrow">{tx('hero.eyebrow')}</span>
          <h1 className="hero2-title">
            {tx('hero.titulo1')} <em>{tx('hero.titulo2_em')}</em>
          </h1>
          <p className="hero2-sub">{tx('hero.subtitulo')}</p>
          <div className="hero2-actions">
            <Link to="/horarios" className="btn-primary">{tx('hero.cta_horario')}</Link>
            {promo && (
              <Link to={promo.url} className="hero2-promo">
                <span className="hero2-promo-titulo">{promo.titulo}</span>
                <span className="hero2-promo-precio">{formatEuros(promo.precio_euros)}</span>
                <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            )}
          </div>
        </div>
        <div className="hero2-visual">
          <div className={'hero2-foto' + (foto ? '' : ' hero2-foto--vacia')}>
            {foto ? <img src={foto.url} alt={foto.pie || t('galeria.alt_portada')} /> : <Flower2 size={56} strokeWidth={0.8} />}
          </div>
          <ProximasClasesCard />
        </div>
      </div>
    </section>
  );
}

// Visor de fotos en ventana (como los vídeos): Esc/clic fuera cierra, flechas navegan.
function FotoModal({ fotos, inicio, onClose }) {
  const { t } = useTranslation();
  const [i, setI] = useState(inicio);
  const n = fotos.length;
  const ir = useCallback((d) => setI((x) => (x + d + n) % n), [n]);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') ir(-1);
      else if (e.key === 'ArrowRight') ir(1);
    };
    document.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener('keydown', onKey); };
  }, [onClose, ir]);
  const f = fotos[i];
  const texto = f.frase || f.pie || '';
  return createPortal(
    <div className="galeria-modal" role="dialog" aria-modal="true" aria-label={texto || t('galeria.alt_portada')} onClick={onClose}>
      <div className="galeria-modal-inner" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="galeria-cerrar" onClick={onClose} aria-label={t('galeria.cerrar')}><X size={20} /></button>
        <figure className="galeria-figura">
          <img src={f.url} alt={texto || t('galeria.alt_portada')} />
          {texto && <figcaption>{texto}</figcaption>}
        </figure>
        {n > 1 && (
          <div className="galeria-controles">
            <button type="button" onClick={() => ir(-1)} aria-label={t('galeria.anterior')}><ChevronLeft size={18} /></button>
            <span>{i + 1} / {n}</span>
            <button type="button" onClick={() => ir(1)} aria-label={t('galeria.siguiente')}><ChevronRight size={18} /></button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// Abre la foto solo si fue un clic, no un arrastre del carrusel.
function useClicSinArrastre(abrir) {
  const x0 = useRef(0);
  return {
    onPointerDown: (e) => { x0.current = e.clientX; },
    onClick: (e, idx) => { if (Math.abs(e.clientX - x0.current) < 6) abrir(idx); },
  };
}

// Carrusel horizontal de la galería de portada (scroll-snap + flechas).
function CarruselGaleria({ fotos }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [abierta, setAbierta] = useState(null);
  const cerrar = useCallback(() => setAbierta(null), []);
  const clic = useClicSinArrastre(setAbierta);
  if (!fotos || fotos.length < 2) return null;
  const mover = (dir) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };
  return (
    <section className="galeria-portada" aria-label={t('galeria.titulo')}>
      <header className="section-head">
        <span className="section-eyebrow">{t('galeria.eyebrow')}</span>
        <h2 className="section-title">{t('galeria.titulo')}</h2>
      </header>
      <div className="carrusel">
        <button type="button" className="carrusel-nav carrusel-nav--prev" onClick={() => mover(-1)} aria-label={t('galeria.anterior')}>
          <ChevronLeft size={20} strokeWidth={1.5} />
        </button>
        <div className="carrusel-pista" ref={ref}>
          {fotos.map((f, idx) => (
            <figure key={f.id} className="carrusel-item" style={{ cursor: 'zoom-in' }} onPointerDown={clic.onPointerDown} onClick={(e) => clic.onClick(e, idx)}>
              <img src={f.url} alt={f.pie || t('galeria.alt_portada')} loading="lazy" />
              {f.pie && <figcaption>{f.pie}</figcaption>}
            </figure>
          ))}
        </div>
        <button type="button" className="carrusel-nav carrusel-nav--next" onClick={() => mover(1)} aria-label={t('galeria.siguiente')}>
          <ChevronRight size={20} strokeWidth={1.5} />
        </button>
      </div>
      {abierta !== null && <FotoModal fotos={fotos} inicio={abierta} onClose={cerrar} />}
    </section>
  );
}

// «Foto del día»: carrusel horizontal que se arrastra con el ratón (PC) y con el dedo (móvil).
// Cada día el backend publica una foto nueva con una frase de filosofía de vida y yoga.
function useArrastreRaton(ref, deps) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let activo = false, x0 = 0, left0 = 0;
    const abajo = (e) => {
      // Con dedo/lápiz el navegador ya desplaza solo; aquí solo el ratón.
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      activo = true; x0 = e.clientX; left0 = el.scrollLeft;
      el.classList.add('is-arrastrando');
    };
    const mover = (e) => { if (activo) el.scrollLeft = left0 - (e.clientX - x0); };
    const soltar = () => { if (!activo) return; activo = false; el.classList.remove('is-arrastrando'); };
    const noDrag = (e) => e.preventDefault(); // evita arrastrar la imagen como archivo
    el.addEventListener('pointerdown', abajo);
    el.addEventListener('dragstart', noDrag);
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', soltar);
    return () => {
      el.removeEventListener('pointerdown', abajo);
      el.removeEventListener('dragstart', noDrag);
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', soltar);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

// Zoom suave: acerca la foto para reducir las franjas vacías del marco (16/15),
// con un tope para no recortar demasiado (la marca de agua va dentro de la foto).
function zoomSuave(e) {
  const im = e.currentTarget;
  if (!im.naturalWidth || !im.naturalHeight) return;
  const marco = 16 / 15;
  const r = im.naturalWidth / im.naturalHeight;
  const cubre = r > marco ? r / marco : marco / r;
  im.style.setProperty('--zoom', Math.min(cubre, 1.18).toFixed(3));
}

function GaleriaDiaria() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const [estado, setEstado] = useState({ fotos: [], hoy: null });
  const ref = useRef(null);
  const [abierta, setAbierta] = useState(null);
  const cerrar = useCallback(() => setAbierta(null), []);
  const clic = useClicSinArrastre(setAbierta);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/galeria-diaria?idioma=${lang}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
      .then((j) => { if (!cancelled) setEstado({ fotos: j.data || [], hoy: j.hoy || null }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [lang]);

  useArrastreRaton(ref, [estado.fotos.length]);

  if (estado.fotos.length === 0) return null; // sin fotos publicadas: la sección no aparece
  const mover = (dir) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };
  const fechaCorta = (iso) => {
    try { return new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long' }).format(new Date(iso + 'T12:00:00')); }
    catch (_) { return iso; }
  };

  return (
    <section className="galeria-diaria" aria-label={t('galeria_diaria.titulo')}>
      <header className="section-head">
        <span className="section-eyebrow">{t('galeria_diaria.eyebrow')}</span>
        <h2 className="section-title">{t('galeria_diaria.titulo')}</h2>
      </header>
      <div className="carrusel">
        <button type="button" className="carrusel-nav carrusel-nav--prev" onClick={() => mover(-1)} aria-label={t('galeria.anterior')}>
          <ChevronLeft size={20} strokeWidth={1.5} />
        </button>
        <div className="carrusel-pista diaria-pista" ref={ref} tabIndex={0} role="list">
          {estado.fotos.map((f, idx) => (
            <figure key={f.id} className="diaria-item" role="listitem" style={{ cursor: 'zoom-in' }} onPointerDown={clic.onPointerDown} onClick={(e) => clic.onClick(e, idx)}>
              <div className="diaria-foto diaria-foto--blur" style={{ '--foto': `url("${f.url}")` }}>
                <img src={f.url} alt={f.frase || t('galeria.alt_portada')} loading="lazy" draggable="false" onLoad={zoomSuave} />
                {f.fecha !== estado.hoy && <span className="diaria-fecha">{fechaCorta(f.fecha)}</span>}
              </div>
              {f.frase && <figcaption className="diaria-frase">{f.frase}</figcaption>}
            </figure>
          ))}
        </div>
        <button type="button" className="carrusel-nav carrusel-nav--next" onClick={() => mover(1)} aria-label={t('galeria.siguiente')}>
          <ChevronRight size={20} strokeWidth={1.5} />
        </button>
      </div>
      {estado.fotos.length > 1 && <p className="diaria-pista-ayuda">{t('galeria_diaria.desliza')}</p>}
      {abierta !== null && <FotoModal fotos={estado.fotos} inicio={abierta} onClose={cerrar} />}
    </section>
  );
}

function VideoDiario() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const [estado, setEstado] = useState({ videos: [], hoy: null });
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/galeria-video-diaria?idioma=${lang}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
      .then((j) => { if (!cancelled) setEstado({ videos: j.data || [], hoy: j.hoy || null }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [lang]);

  useArrastreRaton(ref, [estado.videos.length]);

  if (estado.videos.length === 0) return null;
  const mover = (dir) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };
  const fechaCorta = (iso) => {
    try { return new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long' }).format(new Date(iso + 'T12:00:00')); }
    catch (_) { return iso; }
  };

  return (
    <section className="galeria-diaria video-diario" aria-label={t('video_diario.titulo')}>
      <header className="section-head">
        <span className="section-eyebrow">{t('video_diario.eyebrow')}</span>
        <h2 className="section-title">{t('video_diario.titulo')}</h2>
      </header>
      <div className="carrusel">
        <button type="button" className="carrusel-nav carrusel-nav--prev" onClick={() => mover(-1)} aria-label={t('galeria.anterior')}>
          <ChevronLeft size={20} strokeWidth={1.5} />
        </button>
        <div className="carrusel-pista diaria-pista" ref={ref} tabIndex={0} role="list">
          {estado.videos.map((v) => (
            <figure key={v.id} className="diaria-item video-item" role="listitem">
              <div className="diaria-foto diaria-foto--blur video-marco" style={{ '--foto': `url("${v.url}")` }}>
                <video
                  className="video-calido"
                  src={v.video}
                  poster={v.url}
                  controls
                  playsInline
                  preload="none"
                  controlsList="nodownload"
                  aria-label={v.frase || t('video_diario.play')}
                />
                <span className="video-marca" aria-hidden="true">I · DO · YOGA</span>
                {v.fecha !== estado.hoy && <span className="diaria-fecha">{fechaCorta(v.fecha)}</span>}
              </div>
              {v.frase && <figcaption className="diaria-frase">{v.frase}</figcaption>}
            </figure>
          ))}
        </div>
        <button type="button" className="carrusel-nav carrusel-nav--next" onClick={() => mover(1)} aria-label={t('galeria.siguiente')}>
          <ChevronRight size={20} strokeWidth={1.5} />
        </button>
      </div>
      {estado.videos.length > 1 && <p className="diaria-pista-ayuda">{t('video_diario.desliza')}</p>}
    </section>
  );
}

// mejora-web F4: «Vídeos gratuitos» (máx. 4, filtro por categoría, banner de membresía).
function VideosGratuitosSection() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const { data, membresia } = useVideos(lang, { soloPublicos: true });
  const [reproduciendo, setReproduciendo] = useState(null);
  const cerrar = useCallback(() => setReproduciendo(null), []);
  if (!data || data.length === 0) return null;
  const lista = data.slice(0, 4);
  return (
    <section id="videos" className="videos-gratis">
      <header className="section-head">
        <span className="section-eyebrow">{t('videos.estudio_eyebrow')}</span>
        <h2 className="section-title">{t('videos.gratis_titulo')}</h2>
      </header>
      <div className="videos-gratis-inner">
        <div className="videos-grid">
          {lista.map((v) => <VideoCard key={v.id} video={v} onPlay={setReproduciendo} onBloqueado={() => {}} />)}
        </div>
        {!membresia && <BannerMembresia logueado={isAuthenticated} compacto />}
        <div className="eventos-cta-wrap">
          <Link to="/videos" className="btn-ghost eventos-cta">{t('videos.ver_biblioteca')} <ArrowRight size={14} strokeWidth={1.5} /></Link>
        </div>
      </div>
      {reproduciendo && <ReproductorModal video={reproduciendo} onClose={cerrar} />}
    </section>
  );
}

export default function Home() {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const portada = usePortada(lang);
  return (
    <TextosPortadaCtx.Provider value={portada.contenido}>
      <HeroPortada portada={portada} />
      <ClasesSection />
      <CarruselGaleria fotos={portada.galeria} />
      <GaleriaDiaria />
      <VideoDiario />
      <VideosGratuitosSection />
      <BonosSection />
      <div className="empresas-cta-wrap"><EmpresasCTA /></div>
      <ProfesoresSection />
      <EventosSection />
      <RedesSection />
    </TextosPortadaCtx.Provider>
  );
}

function formatFechaCorta(iso, lang) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  const map = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  return new Intl.DateTimeFormat(map[lang] || 'es-ES', {
    day: 'numeric', month: 'long',
  }).format(d);
}

function EventoCard({ evento }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const navigate = useNavigate();
  const ir = () => evento.slug && navigate(`/eventos/${evento.slug}`);
  const lleno = evento.plazas_restantes === 0;
  return (
    <article className="evento-card" onClick={ir} role="button" tabIndex={0}
             onKeyDown={(e) => { if (e.key === 'Enter') ir(); }}>
      <div className="evento-card-foto">
        {evento.imagen_url ? (
          <img src={evento.imagen_url} alt={evento.nombre} />
        ) : (
          <div className="evento-card-foto-placeholder">
            <Sparkles size={32} strokeWidth={1.2} />
          </div>
        )}
        {lleno && <div className="evento-card-badge">{t('eventos.lleno')}</div>}
      </div>
      <div className="evento-card-body">
        <div className="evento-card-fecha"><Calendar size={14} strokeWidth={1.5} /> {formatFechaCorta(evento.fecha, lang)} · {evento.hora_inicio}</div>
        <h3 className="evento-card-title">{evento.nombre}</h3>
        <p className="evento-card-desc">{evento.descripcion}</p>
        <div className="evento-card-meta">
          <span><MapPin size={12} strokeWidth={1.5} /> {evento.lugar}</span>
          <span><Users size={12} strokeWidth={1.5} /> {evento.plazas_restantes} / {evento.aforo_maximo}</span>
        </div>
        <div className="evento-card-cta">
          {evento.precio_euros > 0
            ? <span className="evento-card-precio">{formatEuros(evento.precio_euros)}</span>
            : <span className="evento-card-gratis">{t('eventos.gratis')}</span>}
          <span className="evento-card-link">{t('eventos.ver_detalles')} <ArrowRight size={14} strokeWidth={1.5} /></span>
        </div>
      </div>
    </article>
  );
}

// mejora-web F3: formulario «Avísame del próximo evento»
function AvisameEvento() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const [email, setEmail] = useState('');
  const [estado, setEstado] = useState('idle'); // idle | enviando | ok | error
  const enviar = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) { setEstado('invalido'); return; }
    setEstado('enviando');
    try {
      const r = await fetch(`${API_BASE}/api/eventos/avisame`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), idioma: lang }),
      });
      setEstado(r.ok ? 'ok' : 'error');
    } catch { setEstado('error'); }
  };
  return (
    <div className="avisame">
      <Mail size={28} strokeWidth={1.2} className="avisame-icon" />
      <h3 className="avisame-titulo">{t('eventos.avisame_titulo')}</h3>
      <p className="avisame-texto">{t('eventos.avisame_texto')}</p>
      {estado === 'ok' ? (
        <p className="avisame-ok"><Check size={16} strokeWidth={1.8} /> {t('eventos.avisame_ok')}</p>
      ) : (
        <form className="avisame-form" onSubmit={enviar} noValidate>
          <label className="sr-only" htmlFor="avisame-email">{t('eventos.avisame_placeholder')}</label>
          <input
            id="avisame-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder={t('eventos.avisame_placeholder')}
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (estado !== 'enviando') setEstado('idle'); }}
            required
          />
          <button type="submit" className="btn-primary" disabled={estado === 'enviando'}>
            {t('eventos.avisame_btn')}
          </button>
        </form>
      )}
      {estado === 'invalido' && <p className="avisame-error">{t('eventos.avisame_invalido')}</p>}
      {estado === 'error' && <p className="avisame-error">{t('eventos.avisame_error')}</p>}
      <p className="avisame-legal">{t('eventos.avisame_legal')}</p>
    </div>
  );
}

// mejora-web F3: visor de galería (lightbox) de una edición pasada
function GaleriaModal({ evento, onClose }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const [fotos, setFotos] = useState(null);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/eventos/${evento.slug}/galeria?idioma=${lang}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
      .then((j) => { if (!cancelled) setFotos(j.data || []); })
      .catch(() => { if (!cancelled) setFotos(evento.imagen_url ? [{ id: 'p', url: evento.imagen_url }] : []); });
    return () => { cancelled = true; };
  }, [evento.slug, evento.imagen_url, lang]);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIdx((i) => i + 1);
      if (e.key === 'ArrowLeft') setIdx((i) => i - 1);
    };
    document.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener('keydown', onKey); };
  }, [onClose]);
  const n = fotos ? fotos.length : 0;
  const actual = n ? fotos[((idx % n) + n) % n] : null;
  return (
    <div className="galeria-modal" role="dialog" aria-modal="true" aria-label={evento.nombre} onClick={onClose}>
      <div className="galeria-modal-inner" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="galeria-cerrar" onClick={onClose} aria-label={t('galeria.cerrar')}>
          <X size={20} strokeWidth={1.5} />
        </button>
        <h3 className="galeria-modal-titulo">{evento.nombre}</h3>
        {fotos === null ? (
          <div className="galeria-cargando" />
        ) : n === 0 ? (
          <p className="galeria-vacia">{t('galeria.vacia')}</p>
        ) : (
          <>
            <figure className="galeria-figura">
              <img src={actual.url} alt={actual.pie || evento.nombre} />
              {actual.pie && <figcaption>{actual.pie}</figcaption>}
            </figure>
            {n > 1 && (
              <div className="galeria-controles">
                <button type="button" onClick={() => setIdx((i) => i - 1)} aria-label={t('galeria.anterior')}><ChevronLeft size={20} /></button>
                <span>{(((idx % n) + n) % n) + 1} / {n}</span>
                <button type="button" onClick={() => setIdx((i) => i + 1)} aria-label={t('galeria.siguiente')}><ChevronRight size={20} /></button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function EventoPasadoCard({ evento, onGaleria }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  return (
    <article className="evento-pasado">
      <button type="button" className="evento-pasado-foto" onClick={() => onGaleria(evento)}>
        {evento.imagen_url
          ? <img src={evento.imagen_url} alt={evento.nombre} loading="lazy" />
          : <div className="evento-card-foto-placeholder"><Sparkles size={28} strokeWidth={1.2} /></div>}
        <span className="evento-pasado-badge">{t('eventos.finalizado')}</span>
      </button>
      <div className="evento-pasado-body">
        <span className="evento-pasado-fecha">{formatFechaCorta(evento.fecha, lang)}</span>
        <h4 className="evento-pasado-titulo">{evento.nombre}</h4>
        <button type="button" className="evento-pasado-galeria" onClick={() => onGaleria(evento)}>
          <Images size={14} strokeWidth={1.5} /> {t('eventos.ver_galeria')}
        </button>
      </div>
    </article>
  );
}

function EventosSection() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [galeria, setGaleria] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/eventos?idioma=${lang}`)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))
      .then((j) => {
        if (!cancelled) setDatos({ proximos: (j.proximos || []).slice(0, 3), pasados: (j.pasados || []).slice(0, 3) });
      })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [lang]);
  const cerrarGaleria = useCallback(() => setGaleria(null), []);

  return (
    <section id="eventos" className="eventos">
      <header className="section-head">
        <span className="section-eyebrow">{t('eventos.eyebrow')}</span>
        <h2 className="section-title">{t('eventos.titulo')}</h2>
      </header>
      {error ? (
        <div className="clases-error">{t('eventos.error_carga')}</div>
      ) : datos === null ? (
        <div className="eventos-grid">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="evento-card evento-skeleton" aria-hidden="true" />
          ))}
        </div>
      ) : (
        <>
          {datos.proximos.length === 0 ? (
            <AvisameEvento />
          ) : (
            <>
              <div className="eventos-grid">
                {datos.proximos.map((e) => <EventoCard key={e.id} evento={e} />)}
              </div>
              <div className="eventos-cta-wrap">
                <Link to="/eventos" className="btn-ghost eventos-cta">
                  {t('eventos.ver_todos')} <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
              </div>
            </>
          )}
          {datos.pasados.length > 0 && (
            <div className="eventos-pasados">
              <h3 className="eventos-pasados-titulo">{t('eventos.ediciones_anteriores')}</h3>
              <div className="eventos-pasados-grid">
                {datos.pasados.map((e) => <EventoPasadoCard key={e.id} evento={e} onGaleria={setGaleria} />)}
              </div>
            </div>
          )}
        </>
      )}
      {galeria && <GaleriaModal evento={galeria} onClose={cerrarGaleria} />}
    </section>
  );
}

function RedesSection() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/redes/instagram`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
      .then((j) => { if (!cancelled) setData(j); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, []);

  if (error) return null; // Si la API cae, oculta sección. No queremos romper home.
  const handle = data?.handle || 'anyacontrera';
  const profileUrl = data?.profile_url || `https://www.instagram.com/${handle}/`;
  const posts = data?.posts || [];
  const cargando = data === null;

  return (
    <section id="redes" className="redes-section">
      <header className="section-head">
        <span className="section-eyebrow">{t('redes.eyebrow')}</span>
        <h2 className="section-title">{t('redes.titulo')}</h2>
        <p className="section-sub">
          <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="redes-handle-link">
            <Instagram size={14} strokeWidth={1.5} /> @{handle}
          </a>
        </p>
      </header>

      {cargando ? (
        <div className="redes-grid">
          {Array.from({ length: 10 }).map((_, i) => <div key={i} className="redes-card redes-card--skeleton" />)}
        </div>
      ) : posts.length === 0 ? (
        <div className="redes-empty">
          <Instagram size={32} strokeWidth={1.2} />
          <p>{t('redes.empty')}</p>
          <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost redes-cta-empty">
            {t('redes.cta_seguir', { handle })} <ArrowRight size={14} strokeWidth={1.5} />
          </a>
        </div>
      ) : (
        <>
          <div className="redes-grid">
            {posts.map((p) => <RedesCard key={p.instagram_id} post={p} />)}
          </div>
          <div className="redes-cta-wrap">
            <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost">
              {t('redes.cta_ver_mas')} <ArrowRight size={14} strokeWidth={1.5} />
            </a>
          </div>
        </>
      )}
    </section>
  );
}

function RedesCard({ post }) {
  const isVideo = post.media_type === 'VIDEO';
  const isCarousel = post.media_type === 'CAROUSEL_ALBUM';
  // Para video, usar thumbnail si existe, si no media_url (Meta a veces da el primer frame).
  const src = isVideo ? (post.thumbnail_url || post.media_url) : post.media_url;
  const captionTrim = post.caption
    ? (post.caption.length > 90 ? post.caption.slice(0, 87) + '…' : post.caption)
    : '';
  return (
    <a href={post.permalink} target="_blank" rel="noopener noreferrer" className="redes-card">
      <div className="redes-card-foto">
        <img src={src} alt={captionTrim || 'Instagram post'} loading="lazy" />
        {isVideo && <span className="redes-card-icon"><Play size={16} strokeWidth={1.5} fill="white" /></span>}
        {isCarousel && <span className="redes-card-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M8 4h13M8 12h13M8 20h13M3 4h.01M3 12h.01M3 20h.01"/></svg></span>}
      </div>
      {captionTrim && <div className="redes-card-caption">{captionTrim}</div>}
    </a>
  );
}
